// Trading during the draft (DRAFT_ROOM.md "Trading during the draft", B-345,
// B-346): the sheet that builds an offer, the offers to me under the clock,
// and the offers I am waiting on.
//
// PRESENTATION ONLY. What a team has to trade is read off the board the room
// sent — its picks to come and the players it has — and the room says what is
// allowed and refuses the rest, so nothing here is a rule but the one the
// Offer button shows before it is pressed: the same number each side.
//
// NO VALUE ON ANYTHING. A pick shows its number and a player his position and
// where he went; no trade is called fair or good (tools, not instructions).

import { useEffect, useMemo, useState } from 'react';
import type { DraftTradeItem, DraftTradeItemRef, DraftTradeOffer, DraftView } from '@fsp/domain';
import { PositionPill, TeamMark } from '../../kit';
import {
  Button,
  Callout,
  Caption,
  Card,
  Dialog,
  Line,
  ListRow,
  RowName,
  RowSub,
  Segments,
  Stack,
  Text,
  Tick,
} from '../../kit/ui';
import { useTier } from '../../shell/useTier';
import { DetailSheet } from '../../templates/DetailSheet';
import { pickLabel } from './research';
import { clubLabel } from '../../sports/registry';

/** What the room's trading moves are, when it has them. */
export interface TradeActions {
  offer: (
    toTeamId: string,
    give: DraftTradeItemRef[],
    get: DraftTradeItemRef[],
    replaces: string | null,
  ) => void;
  answer: (offerId: string, accept: boolean) => void;
  withdraw: (offerId: string) => void;
}

/** One thing a team could put in a trade, as the sheet lists it. */
interface Tradeable {
  key: string;
  ref: DraftTradeItemRef;
  name: string;
  sub: string;
  position: string | null;
}

/** A trade item's key, the same for a ref and for the item the room sends back. */
function keyOf(item: DraftTradeItemRef | DraftTradeItem): string {
  if (item.kind === 'pick') return `pick:${String(item.round)}:${item.teamId}`;
  return `player:${'playerId' in item ? item.playerId : item.player.id}`;
}

function managerOf(view: DraftView, teamId: string): string {
  const seat = view.seats.find((s) => s.team.id === teamId);
  if (seat === undefined) return 'a team';
  return seat.team.isMine ? 'you' : seat.team.ownerName;
}

/**
 * What a team has to trade now, from the board: its picks to come, the one
 * on the clock among them, and the players it has, a trade's included.
 */
export function tradeablesOf(view: DraftView, teamId: string): Tradeable[] {
  const picks: Tradeable[] = view.board
    .filter((c) => c.player === null && c.teamId === teamId)
    .map((c) => ({
      key: keyOf({ kind: 'pick', round: c.round, teamId: c.originalTeamId ?? c.teamId }),
      ref: { kind: 'pick', round: c.round, teamId: c.originalTeamId ?? c.teamId },
      name: `Pick ${pickLabel(c.round, c.slot)}`,
      sub: [
        `Round ${String(c.round)}`,
        c.originalTeamId === null ? '' : `via ${managerOf(view, c.originalTeamId)}`,
        c.isCurrent ? 'on the clock' : '',
      ]
        .filter(Boolean)
        .join(' · '),
      position: null,
    }));
  const players: Tradeable[] = view.board
    .filter((c) => c.player !== null && (c.heldBy ?? c.teamId) === teamId)
    .map((c) => ({
      key: `player:${c.player!.id}`,
      ref: { kind: 'player', playerId: c.player!.id },
      name: c.player!.name,
      sub: [
        c.player!.proTeam === null ? 'FA' : clubLabel(c.player!.proTeam),
        `taken ${pickLabel(c.round, c.slot)}`,
        c.heldBy === null ? '' : `from ${managerOf(view, c.teamId)}`,
      ]
        .filter(Boolean)
        .join(' · '),
      position: c.player!.positions[0] ?? null,
    }));
  return [...picks, ...players];
}

/** Things in a trade, in words: *"3.07 and J. Allen"*. */
export function itemsWords(items: readonly DraftTradeItem[]): string {
  const words = items.map((i) =>
    i.kind === 'pick' ? pickLabel(i.round, i.slot) : i.player.shortName,
  );
  if (words.length <= 1) return words.join('');
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]!}`;
}

/** What opens the sheet: whom with, what is ticked to start, and the offer it counters. */
export interface TradeSeed {
  partnerTeamId: string;
  give: string[];
  get: string[];
  replaces: string | null;
}

/** A counter starts from the offer as it was: what they asked of me, and what they gave. */
export function counterSeed(offer: DraftTradeOffer): TradeSeed {
  return {
    partnerTeamId: offer.fromTeamId,
    give: offer.get.map(keyOf),
    get: offer.give.map(keyOf),
    replaces: offer.id,
  };
}

function Picker({
  items,
  picked,
  onToggle,
  emptyText,
}: {
  items: Tradeable[];
  picked: string[];
  onToggle: (key: string) => void;
  emptyText: string;
}) {
  if (items.length === 0) return <div className="fsp-empty">{emptyText}</div>;
  return (
    <Card>
      {items.map((t) => {
        const on = picked.includes(t.key);
        return (
          <ListRow
            key={t.key}
            lead={
              <Line gap={2}>
                <Tick on={on} />
                {t.position === null ? null : <PositionPill pos={t.position} size="sm" />}
              </Line>
            }
            leadSize={t.position === null ? 'sm' : 'xl'}
            checked={on}
            selected={on}
            onClick={() => onToggle(t.key)}
          >
            <RowName>{t.name}</RowName>
            <RowSub>{t.sub}</RowSub>
          </ListRow>
        );
      })}
    </Card>
  );
}

/**
 * The trade sheet: my side and theirs, ticked, and Offer when the two are
 * the same size. What happens on Accept is said before Offer is pressed.
 */
export function TradeSheet({
  view,
  seed,
  actions,
  onClose,
}: {
  view: DraftView;
  seed: TradeSeed | null;
  actions: TradeActions;
  onClose: () => void;
}) {
  const phone = useTier() === 'phone';
  const [give, setGive] = useState<string[]>([]);
  const [get, setGet] = useState<string[]>([]);
  const [side, setSide] = useState<'give' | 'get'>('get');
  const key = seed === null ? '' : JSON.stringify(seed);
  useEffect(() => {
    setGive(seed?.give ?? []);
    setGet(seed?.get ?? []);
    setSide('get');
  }, [key]);

  const mine = useMemo(
    () => (view.myTeamId === null ? [] : tradeablesOf(view, view.myTeamId)),
    [view],
  );
  const theirs = useMemo(
    () => (seed === null ? [] : tradeablesOf(view, seed.partnerTeamId)),
    [view, seed],
  );
  const partner =
    seed === null ? null : (view.seats.find((s) => s.team.id === seed.partnerTeamId) ?? null);
  if (seed === null || partner === null) {
    return <DetailSheet opened={false} onClose={onClose} children={null} />;
  }
  // WHAT THE BOARD HAS MOVED PAST falls out of the selection: a pick made while
  // the sheet was open is not offered.
  const giving = give.filter((k) => mine.some((t) => t.key === k));
  const getting = get.filter((k) => theirs.some((t) => t.key === k));
  const toggle = (list: string[], set: (next: string[]) => void, k: string) =>
    set(list.includes(k) ? list.filter((x) => x !== k) : [...list, k]);
  const even = giving.length > 0 && giving.length === getting.length;
  const refs = (keys: string[], from: Tradeable[]) =>
    keys.flatMap((k) => from.find((t) => t.key === k)?.ref ?? []);

  const giveList = (
    <Picker
      items={mine}
      picked={giving}
      onToggle={(k) => toggle(giving, setGive, k)}
      emptyText="You have nothing left to trade."
    />
  );
  const getList = (
    <Picker
      items={theirs}
      picked={getting}
      onToggle={(k) => toggle(getting, setGet, k)}
      emptyText={`${partner.team.ownerName} has nothing left to trade.`}
    />
  );

  return (
    <DetailSheet
      opened
      onClose={onClose}
      title={seed.replaces ? 'Counter offer' : `Trade with ${partner.team.ownerName}`}
      wide
    >
      <Stack gap={4}>
        <Line gap={2}>
          <TeamMark team={partner.team} size={22} />
          <Text weight="strong">{partner.team.name}</Text>
        </Line>
        {phone ? (
          <>
            <Segments
              label="Which side"
              value={side}
              onChange={setSide}
              items={[
                { id: 'get', label: `You get (${String(getting.length)})` },
                { id: 'give', label: `You give (${String(giving.length)})` },
              ]}
            />
            {side === 'get' ? getList : giveList}
          </>
        ) : (
          <div className="fsp-trade-sides">
            <Stack gap={2}>
              <Caption>You get</Caption>
              {getList}
            </Stack>
            <Stack gap={2}>
              <Caption>You give</Caption>
              {giveList}
            </Stack>
          </div>
        )}
        {/* THE COUNT AND OFFER STAY IN VIEW under a list of fifteen rounds. */}
        <div className="fsp-dr-tradefoot">
          <Text
            size="sm"
            tone={even || giving.length + getting.length === 0 ? 'secondary' : 'warn'}
            block
          >
            You give {giving.length} · you get {getting.length}. Each side gives the same number, so
            every roster ends the same size.
          </Text>
          <Button
            variant="primary"
            size="md"
            full
            disabled={!even}
            onClick={() => {
              actions.offer(
                partner.team.id,
                refs(giving, mine),
                refs(getting, theirs),
                seed.replaces,
              );
              onClose();
            }}
          >
            {seed.replaces ? 'Send counter' : `Offer to ${partner.team.ownerName}`}
          </Button>
          <Text size="xs" tone="secondary" block>
            {/* SAID BEFORE IT IS SENT: what an accept does, and that only the
                commissioner can take it back. */}
            Only {partner.team.ownerName} sees this offer until they answer. If they accept, the
            trade happens at once and everybody in the league sees it; only the commissioner can
            undo it.
          </Text>
        </div>
      </Stack>
    </DetailSheet>
  );
}

/**
 * An offer to me, under the clock until I answer it: Accept, which asks once
 * more, Counter, which opens the sheet with it, and Decline.
 */
export function OfferToMe({
  view,
  offer,
  actions,
  onCounter,
  usable,
}: {
  view: DraftView;
  offer: DraftTradeOffer;
  actions: TradeActions;
  onCounter: (seed: TradeSeed) => void;
  usable: boolean;
}) {
  const [asking, setAsking] = useState(false);
  const from = managerOf(view, offer.fromTeamId);
  const gets = itemsWords(offer.give);
  const gives = itemsWords(offer.get);
  return (
    <Callout tone="accent">
      <span className="fsp-dr-notice">
        <span>
          {from} offers {gets} for your {gives}.
        </span>
        <span className="fsp-dr-noticebtns">
          <Button size="xs" variant="primary" disabled={!usable} onClick={() => setAsking(true)}>
            Accept
          </Button>
          <Button size="xs" disabled={!usable} onClick={() => onCounter(counterSeed(offer))}>
            Counter
          </Button>
          <Button
            size="xs"
            variant="quiet"
            disabled={!usable}
            onClick={() => actions.answer(offer.id, false)}
          >
            Decline
          </Button>
        </span>
      </span>
      <Dialog opened={asking} onClose={() => setAsking(false)} title="Accept this trade?">
        <Stack gap={3}>
          <Text block>
            Your {gives} {offer.get.length === 1 ? 'goes' : 'go'} to {from}, and {gets}{' '}
            {offer.give.length === 1 ? 'comes' : 'come'} to you, at once. Only the commissioner can
            undo a trade.
          </Text>
          <Line gap={2}>
            <Button
              variant="primary"
              onClick={() => {
                setAsking(false);
                actions.answer(offer.id, true);
              }}
            >
              Accept the trade
            </Button>
            <Button variant="quiet" onClick={() => setAsking(false)}>
              Not yet
            </Button>
          </Line>
        </Stack>
      </Dialog>
    </Callout>
  );
}

/** The offers I am waiting on, with Withdraw. */
export function MyOffers({
  view,
  actions,
  usable,
}: {
  view: DraftView;
  actions: TradeActions;
  usable: boolean;
}) {
  const out = view.trading.offers.filter((o) => o.fromTeamId === view.myTeamId);
  if (out.length === 0) return null;
  return (
    <div className="fsp-dr-note">
      <Caption>Your offers</Caption>
      {out.map((o) => (
        <div key={o.id} className="fsp-row fsp-dr-pickrow">
          <div className="fsp-ui-grow">
            <div className="fsp-name">
              <span className="fsp-name-text">Waiting on {managerOf(view, o.toTeamId)}</span>
            </div>
            <div className="fsp-sub">
              Your {itemsWords(o.give)} for {itemsWords(o.get)}
            </div>
          </div>
          <Button
            size="xs"
            variant="quiet"
            disabled={!usable}
            onClick={() => actions.withdraw(o.id)}
          >
            Withdraw
          </Button>
        </div>
      ))}
    </div>
  );
}

/** Every trade made in this draft, newest first, in words. */
export function TradesMade({ view }: { view: DraftView }) {
  const trades = [...view.trading.trades].reverse();
  if (trades.length === 0) return null;
  return (
    <div className="fsp-dr-note">
      <Caption>Trades</Caption>
      {trades.map((t) => (
        <Text key={t.seq} size="sm" block>
          {managerOf(view, t.fromTeamId)} got {itemsWords(t.get)}; {managerOf(view, t.toTeamId)} got{' '}
          {itemsWords(t.give)}.
        </Text>
      ))}
    </div>
  );
}
